import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Overlay, Popover } from 'react-bootstrap'
import { ReactComponent as Bell } from '../../../../assets/svgs/bell.svg'
import { useNavigate } from 'react-router-dom'
import NotificationItem from './NotificationItem'
import {
  listenToNotifications,
  markAllAsRead,
} from './utils/notificationService'
import { notifyError, notifySuccess } from '../../../../utils/toast'
import useAuthStore from '../../../../stores/authStore'
import { CoreButton } from '../../../../components/buttons/CoreButton'
import { useTheme } from '../../../../contexts/themeContext'
import {
  NotificationsCenterEmptyMessage,
  NotificationsCenterFooter,
  NotificationsCenterHeader,
  NotificationsCenterLoadingSpinner,
  NotificationsCenterPopoverContent,
  NotificationsCenterScrollArea,
  NotificationsCenterSeparator,
  NotificationsCenterTitle,
} from './styled'

const NotificationBadge = React.memo(({ count }) => {
  if (count <= 0) return null
  const { theme } = useTheme()

  return (
    <span
      style={{
        position: 'absolute',
        top: '-0.15rem',
        right: '-0.15rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '1.05rem',
        width: '1.05rem',
        borderRadius: '9999px',
        backgroundColor: theme.darkRed,
        color: 'white',
        fontSize: '0.625rem',
        fontWeight: '500',
      }}
    >
      {count > 9 ? '9+' : count}
    </span>
  )
})

const NotificationPopoverContent = React.memo(
  ({
    loading,
    notifications,
    handleMarkAllAsRead,
    onMarkAsRead,
    onViewAll,
    t,
  }) => {
    const unreadCount = useMemo(
      () => notifications.filter((n) => !n.read).length,
      [notifications],
    )

    return (
      <NotificationsCenterPopoverContent>
        <NotificationsCenterHeader>
          <NotificationsCenterTitle>
            {t('notifications.notificationsCenter.title')}
          </NotificationsCenterTitle>
          {unreadCount > 0 && (
            <CoreButton
              variant="ghost-primary"
              size="sm"
              onClick={handleMarkAllAsRead}
            >
              {t('notifications.markAllAsRead')}
            </CoreButton>
          )}
        </NotificationsCenterHeader>
        <NotificationsCenterSeparator />
        {loading ? (
          <div
            style={{
              padding: '2rem',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <NotificationsCenterLoadingSpinner />
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <NotificationsCenterEmptyMessage>
              {t('notifications.notificationsCenter.noNotifications')}
            </NotificationsCenterEmptyMessage>
          </div>
        ) : (
          <NotificationsCenterScrollArea>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={onMarkAsRead}
              />
            ))}
          </NotificationsCenterScrollArea>
        )}
        <NotificationsCenterSeparator />
        <NotificationsCenterFooter>
          <CoreButton
            variant="outlined-primary"
            size="full"
            onClick={onViewAll}
          >
            {t('notifications.notificationsCenter.viewAll')}
          </CoreButton>
        </NotificationsCenterFooter>
      </NotificationsCenterPopoverContent>
    )
  },
)

function NotificationsCenter() {
  const { t } = useTranslation()
  const { theme } = useTheme()

  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [show, setShow] = useState(false)
  const target = useRef(null)
  const [isPopoverLoaded, setIsPopoverLoaded] = useState(false)

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  )
  const userId = useAuthStore((state) => state.identity?.id)

  useEffect(() => {
    if (!userId) return undefined
    const unsubscribe = listenToNotifications((data) => {
      setNotifications(data)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [userId])

  const handleTogglePopover = useCallback(() => {
    const newShowState = !show
    setShow(newShowState)

    if (newShowState && !isPopoverLoaded) {
      setIsPopoverLoaded(true)
    }
  }, [show, isPopoverLoaded])

  const handleMarkAsRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification,
      ),
    )
  }, [])

  const handleMarkAllAsRead = useCallback(async () => {
    const userUnreadNotifications = notifications.filter((n) => !n.read)

    if (userUnreadNotifications.length === 0) return

    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true })),
    )

    try {
      const success = await markAllAsRead()
      if (success) {
        notifySuccess(t('notifications.markAllAsReadSuccess'))
      }
    } catch (error) {
      setNotifications((prev) =>
        prev.map((notification) =>
          userUnreadNotifications.some((n) => n.id === notification.id)
            ? { ...notification, read: false }
            : notification,
        ),
      )
      notifyError(t('notifications.markAllAsReadFailed'))
    }
  }, [notifications, t])

  const handleViewAll = useCallback(() => {
    navigate('/notifications')
    setShow(false)
  }, [navigate])

  return (
    <>
      <div
        className="position-relative"
        ref={target}
        onClick={handleTogglePopover}
      >
        <CoreButton
          variant="ghost"
          iconOnly
          icon={<Bell color={theme.defaultIconColor} width={20} height={20} />}
        />
        <NotificationBadge count={unreadCount} />
      </div>

      {show && (
        <Overlay
          show={show}
          target={target.current}
          placement="bottom-end"
          container={document.body}
          rootClose
          onHide={() => setShow(false)}
        >
          {(props) => (
            <Popover
              id="notifications-popover"
              {...props}
              style={{
                ...props.style,
                maxWidth: '450px',
                width: '100%',
                padding: 0,
                border: 'none',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <NotificationPopoverContent
                loading={loading}
                notifications={notifications}
                handleMarkAllAsRead={handleMarkAllAsRead}
                onMarkAsRead={handleMarkAsRead}
                onViewAll={handleViewAll}
                t={t}
              />
            </Popover>
          )}
        </Overlay>
      )}
    </>
  )
}

export default React.memo(NotificationsCenter)
